import { useState } from "react";
import {
  Box,
  Toolbar,
  Grid,
  TextField,
  Button,
  Typography,
  IconButton,
  Switch,
} from "@mui/material";
import OpenInNew from "@mui/icons-material/OpenInNew";
import MapIcon from "@mui/icons-material/MapOutlined";
import SimpleAppBar from "components/App/SimpleAppBar";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import Map from "components/Search/Map";
import { fetchSearchResults } from "api";

export default function App(props) {
  // property type
  // wild fire risk
  // flood zone
  // distance to saltwater
  // fire station proximity
  const [filters, updateFilters] = useState({
    state: "",
    city: "",
    zip_code: "",
    year_built: [undefined, undefined],
    stories: "",
    beds_count: [undefined, undefined],
    baths: [undefined, undefined],
    partial_baths_count: [undefined, undefined],
    total_area_sq_ft: [undefined, undefined],
    avg_std: [undefined, undefined],
    best_std: [undefined, undefined],
  });
  const filterList = [
    { label: "State", type: "string", key: "state", component: "TextField" },
    { label: "City", type: "string", key: "city", component: "TextField" },
    {
      label: "Zip Code",
      type: "string",
      key: "zip_code",
      component: "TextField",
    },
    {
      label: "Year Built",
      type: "number",
      key: "year_built",
      component: "Range",
    },
    {
      label: "Stories",
      type: "string",
      key: "stories",
      component: "TextField",
    },
    { label: "Beds", type: "number", key: "beds_count", component: "Range" },
    { label: "Baths", type: "number", key: "baths", component: "Range" },
    {
      label: "Partial Baths",
      type: "number",
      key: "partial_baths_count",
      component: "Range",
    },
    {
      label: "Sqft",
      type: "number",
      key: "total_area_sq_ft",
      component: "Range",
    },
    {
      label: "Replacement Cost (Avg Std)",
      type: "number",
      key: "avg_std",
      component: "Range",
    },
    {
      label: "Replacement Cost (Best Std)",
      type: "number",
      key: "best_std",
      component: "Range",
    },
  ];
  const [searchResults, setSearchResults] = useState([]);
  const [searchResultsLoading, setSearchResultsLoading] = useState(false);
  const onClick = (formatted_street_address, city, state, zip_code) => {
    new Promise((resolve) => {
      if (window.google) {
        return resolve(window.google);
      }
      const script = document.createElement("script");
      const key = process.env.REACT_APP_GOOGLE_KEY;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${key}`;
      script.onload = () => resolve(window.google);
      document.getElementsByTagName("head")[0].appendChild(script);
    }).then((google) => {
      const geocoder = new google.maps.Geocoder();
      const address = `${formatted_street_address}, ${city}, ${state} ${zip_code}`;
      geocoder.geocode({ address: address }, function (results) {
        if (!results.length) {
          return;
        }
        const geoLookup = {};
        results[0].address_components.forEach((g) => {
          g.types.forEach((t) => {
            geoLookup[t] = g;
          });
        });
        const getStreetNumber = () => {
          return (geoLookup["street_number"] || {})["short_name"];
        };
        const getStreetName = () => {
          return (geoLookup["route"] || {})["short_name"];
        };
        const getCity = () => {
          const a = geoLookup["locality"] || {};
          const b = geoLookup["sublocality"] || {};
          const c = geoLookup["sublocality_level_1"] || {};
          return a["long_name"] || b["long_name"] || c["long_name"];
        };
        const getState = () => {
          return (geoLookup["administrative_area_level_1"] || {})["short_name"];
        };
        const getZipCode = () => {
          return (geoLookup["postal_code"] || {})["short_name"];
        };
        const getCounty = () => {
          return (geoLookup["administrative_area_level_2"] || {})["short_name"];
        };
        const formatted_street_address = `${getStreetNumber()} ${getStreetName()}`;
        const city = getCity();
        const state = getState();
        const zip_code = getZipCode();
        const county = getCounty();
        const lat = results[0].geometry.location.lat();
        const lng = results[0].geometry.location.lng();
        window.open(
          `/summary?formatted_street_address=${formatted_street_address}&city=${city}&state=${state}&zip_code=${zip_code}&county=${county}&lat=${lat}&lng=${lng}`
        );
      });
    });
  };
  const columns = [
    {
      field: "id",
      headerName: "",
      width: 90,
      renderCell: (params) => {
        const { formatted_street_address, city, state, zip_code } = params.row;
        return (
          <IconButton
            onClick={() =>
              onClick(formatted_street_address, city, state, zip_code)
            }
          >
            <OpenInNew />
          </IconButton>
        );
      },
    },
    {
      field: "formatted_street_address",
      headerName: "Address",
      width: 300,
    },
    {
      field: "city",
      headerName: "City",
      width: 150,
    },
    {
      field: "state",
      headerName: "State",
      width: 75,
    },
    {
      field: "zip_code",
      headerName: "Zip Code",
      width: 100,
    },
    {
      field: "year_built",
      headerName: "Year Built",
      width: 100,
    },
    {
      field: "stories",
      headerName: "Stories",
      width: 75,
    },
    {
      field: "beds_count",
      headerName: "Beds",
      width: 75,
    },
    {
      field: "baths",
      headerName: "Baths",
      width: 75,
    },
    {
      field: "partial_baths_count",
      headerName: "Partial Baths",
      width: 100,
    },
    {
      field: "total_area_sq_ft",
      headerName: "Sqft",
      width: 75,
    },
    {
      field: "avg_std",
      headerName: "Replacement Cost (Avg Std)",
      width: 200,
      renderCell: (params) => {
        if (params.value) {
          return `$${Math.round(params.value).toLocaleString()}`;
        }
        return params.value;
      },
    },
    {
      field: "best_std",
      headerName: "Replacement Cost (Best Std)",
      width: 200,
      renderCell: (params) => {
        if (params.value) {
          return `$${Math.round(params.value).toLocaleString()}`;
        }
        return params.value;
      },
    },
  ];
  const search = () => {
    setSearchResultsLoading(true);
    fetchSearchResults({
      addresses: {
        state: filters.state || null,
        city: filters.city || null,
        zip_code: filters.zip_code || null,
      },
      structures: {
        year_built: filters.year_built,
        stories: filters.stories || null,
        beds_count: filters.beds_count,
        baths: filters.baths,
        partial_baths_count: filters.partial_baths_count,
        total_area_sq_ft: filters.total_area_sq_ft,
      },
      replacement_costs: {
        avg_std: filters.avg_std,
        best_std: filters.best_std,
      },
    })
      .then((results) => {
        const newResults = results.map((_) => {
          const structures = _.structures || {};
          const replacementCosts = _.replacement_costs || {};
          return { ..._, ...structures, ...replacementCosts };
        });
        setSearchResults(newResults);
        setSearchResultsLoading(false);
      })
      .catch(() => {
        setSearchResultsLoading(false);
      });
  };
  const getComponent = (f) => {
    if (f.component === "TextField") {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {f.label}
          </Grid>
          <Grid item xs={12}>
            <TextField
              size="small"
              style={{ width: "100%" }}
              onChange={(e) => {
                let val = e.target.value || "";
                if (val && f.type === "string") {
                  val = val.toUpperCase();
                } else if (val && f.type === "number") {
                  val = parseFloat(val);
                }
                updateFilters({ ...filters, [f.key]: val });
              }}
              value={filters[f.key]}
            />
          </Grid>
        </Grid>
      );
    } else if (f.component === "Range") {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {f.label}
          </Grid>
          <Grid item xs={6}>
            <TextField
              value={filters[f.key][0]}
              label="Min"
              variant="outlined"
              size="small"
              type="number"
              onChange={(e) => {
                const val = parseFloat(e.target.value, 10);
                updateFilters({
                  ...filters,
                  [f.key]: [
                    val === 0 ? 0 : val || undefined,
                    filters[f.key][1],
                  ],
                });
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              value={filters[f.key][1]}
              label="Max"
              variant="outlined"
              size="small"
              type="number"
              onChange={(e) => {
                const val = parseFloat(e.target.value, 10);
                console.log();
                updateFilters({
                  ...filters,
                  [f.key]: [
                    filters[f.key][0],
                    val === 0 ? 0 : val || undefined,
                  ],
                });
              }}
            />
          </Grid>
        </Grid>
      );
    }
  };

  const [displayMap, setDisplayMap] = useState(true);

  return (
    <Box sx={{ display: "flex" }}>
      <SimpleAppBar profile={props.profile} setProfile={props.setProfile} />
      <Box
        component="main"
        style={{
          backgroundColor: (theme) => theme.palette.grey[100],
          flexGrow: 1,
          height: "100vh",
          overflow: "auto",
        }}
      >
        <Toolbar />
        <Grid container columnSpacing={4} pt={4} px={4}>
          <Grid item width={300}>
            <Typography variant="h6" color="primary" gutterBottom>
              Filters
            </Typography>
            <Grid
              container
              spacing={2}
              style={{
                height: "calc(100vh - 235px)",
                overflow: "auto",
                marginTop: "0.35em",
              }}
            >
              {filterList.map((f, index) => (
                <Grid item style={{ width: "100%" }} key={index}>
                  {getComponent(f)}
                </Grid>
              ))}
            </Grid>
            <Button
              variant="contained"
              onClick={search}
              style={{ marginTop: "32px" }}
            >
              Search
            </Button>
          </Grid>
          <Grid item width={"calc(100vw - 355px)"}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="h6" color="primary" gutterBottom>
                Results
              </Typography>
              <div style={{ display: "flex", alignItems: "center" }}>
                <MapIcon color="primary" />
                <Switch
                  checked={displayMap}
                  onChange={() => setDisplayMap(!displayMap)}
                />
              </div>
            </div>
            <Box sx={{ height: "calc(100vh - 167px)", width: "100%" }}>
              <Map
                style={{ display: displayMap ? undefined : "none" }}
                loading={searchResultsLoading}
                rows={searchResults}
                onClick={onClick}
              />
              {!displayMap ? (
                <DataGrid
                  components={{ Toolbar: GridToolbar }}
                  density="compact"
                  loading={searchResultsLoading}
                  rows={searchResults}
                  columns={columns}
                />
              ) : null}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
